import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import type { ITheme } from '@inter-ikea-kompis/types';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import type {
	PopoverAlignmentEnum,
	PopoverDirectionEnum
} from '@inter-ikea-kompis/component-popover';
import { Popover, PopoverPositioner } from '@inter-ikea-kompis/component-popover';
import { Text } from '@inter-ikea-kompis/component-text';

/**
 * @deprecated This component has been deprecated, see Kompis page for more info.
 *
 * Tooltip class.
 *
 * @event animationEnd When animation end.
 */

export default class Tooltip extends KompisElement {
	// Text to be displayed.
	@property({ type: String })
	public text: string | null = null;

	// Z index.
	@property({ type: Number })
	public zIndex: number | null = null;

	// Max width in pixels.
	@property({ type: Number })
	public maxWidth: number | null = null;

	// Alignment of popover.
	@property({ type: String })
	public alignment: PopoverAlignmentEnum;

	// Direction of popover.
	@property({ type: String })
	public direction: PopoverDirectionEnum;

	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// Enable toggle mode.
	@property({ type: Boolean })
	public toggle = false;

	// Label for the tooltip to set as an aria-label for accessibility reasons.
	@property({ type: String })
	public override ariaLabel: string | null = null;

	// Unique ID text. Tooltip targets should have aria-describedby and tooltip content a corresponding ID for accessibility purposes.
	@property({ type: String })
	public uniqueId: string | null = null;

	// Sets how long the tooltip should be visible for. Set to 0 to disable.
	@property({ type: Number })
	public timeout = 0;

	private visible = false;
	private renderPopover = false;
	private timer: ReturnType<typeof setTimeout> | null = null;

	/**
	 * Renders the component.
	 *
	 * @returns Template.
	 */
	public override render(): RenderResult {
		const { color, border, spacing } = this.theme;
		return html`
			<style>
				:host {
					display: inline-flex;
					position: relative;
				}
				kompis-popover-positioner {
					pointer-events: none;
					position: absolute;
					left: 0;
					right: 0;
					top: 0;
					bottom: 0;
				}
				kompis-text {
					user-select: none;
					-o-user-select: none;
					-moz-user-select: none;
					-khtml-user-select: none;
					-webkit-user-select: none;
				}
				.content {
					color: ${color.text.inverse};
					position: relative;
					background-color: ${color.background.inverse};
					border-radius: ${border.radius.radius4 / 16}rem;
					border-width: 0;
					max-width: ${this.maxWidth ? this.maxWidth / 16 + 'rem' : 'none'};
					padding: ${spacing.space50 / 16}rem ${spacing.space75 / 16}rem;
				}
			</style>
			<slot
				@pointerenter="${this.onEnter.bind(this)}"
				@pointerleave="${this.onLeave.bind(this)}"
				@focusin="${this.onFocusIn.bind(this)}"
				@focusout="${this.onFocusOut.bind(this)}"
				@pointerdown="${this.onClick.bind(this)}"
			></slot>
			${this.getPopover()}
		`;
	}

	public override connectedCallback(): void {
		this.customElements.define('kompis-popover-positioner', PopoverPositioner);
		this.customElements.define('kompis-popover', Popover);
		this.customElements.define('kompis-text', Text);
		super.connectedCallback();
		window.addEventListener('keydown', this.onKeyDownListener);
	}

	public override disconnectedCallback(): void {
		super.disconnectedCallback();
		this.visible = false;
		window.removeEventListener('keydown', this.onKeyDownListener);
	}

	protected override shouldUpdate(
		changedProperties: Map<string, object | number | string | boolean>
	): boolean {
		if (changedProperties.has('toggle')) {
			if (changedProperties.get('toggle')) {
				this.visible = false;
			}
		}
		return true;
	}

	/**
	 * Event handler enter.
	 */
	private onEnter(): void {
		if (this.toggle) {
			return;
		}

		if (this.timer) clearTimeout(this.timer);

		this.visible = true;
		this.renderPopover = true;
		this.requestUpdate();
	}

	/**
	 * Event handler leave.
	 */
	private onLeave(): void {
		if (this.toggle) {
			return;
		}

		// Begin timer
		this.timer = setTimeout(() => {
			this.visible = false;
			this.requestUpdate();
		}, this.timeout);

		this.requestUpdate();
	}

	/**
	 * Event handler focus in.
	 */
	private onFocusIn(): void {
		this.visible = true;
		this.renderPopover = true;
		this.requestUpdate();
	}

	/**
	 * Event handler focus out.
	 */
	private onFocusOut(): void {
		this.visible = false;
		this.requestUpdate();
	}

	/**
	 * Event handler click.
	 */
	private onClick(): void {
		if (!this.toggle) {
			return;
		}

		if (!this.visible) {
			this.visible = true;
			this.renderPopover = true;
		} else {
			this.visible = false;
			this.renderPopover = true;
		}
		this.requestUpdate();
	}

	/**
	 * Cleans up in the DOM.
	 */
	private onAnimationEnd(): void {
		if (this.visible === false) {
			this.renderPopover = false;
			this.dispatchEvent(new CustomEvent('animationEnd', { bubbles: true }));
			this.requestUpdate();
		}
	}

	/**
	 * Closes tooltip on escape.
	 *
	 * @param event Event.
	 */
	private onKeyDownListener = (event: KeyboardEvent): void => {
		if (event.code === 'Escape' && this.visible) {
			if (this.timer) clearTimeout(this.timer);
			this.visible = false;
			this.renderPopover = false;
			this.requestUpdate();
		}
	};

	/**
	 * Gets the popover.
	 *
	 * @returns Template.
	 */
	private getPopover(): RenderResult {
		if (!this.renderPopover) {
			return null;
		}

		return html`
			<kompis-popover-positioner
				.direction="${this.direction}"
				.alignment="${this.alignment}"
				.visible="${this.visible}"
				.theme="${this.theme}"
				.zIndex="${this.zIndex}"
				@animationEnd="${this.onAnimationEnd.bind(this)}"
				@pointerenter="${this.onEnter.bind(this)}"
				@pointerleave="${this.onLeave.bind(this)}"
			>
				<div
					class="content"
					id="${this.uniqueId}"
					aria-label="${this.ariaLabel ? this.ariaLabel : ''}"
					role="tooltip"
					aria-live="polite"
					aria-atomic="true"
				>
					<kompis-text .theme="${this.theme}" .type="${ThemeFontStyleTypeEnum.bodySmall}">
						${this.renderPopover ? this.text : null}
					</kompis-text>
				</div>
			</kompis-popover-positioner>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-tooltip': Tooltip;
	}
}
